import type { Route } from '@angular/router';
import {
  appAuthRoutes,
  authGuard,
  clientPortalAuthGuard,
  roleLandingGuard,
  subPortalAuthGuard,
} from '@vantage-web/auth';
import { SigningInviteStore } from '@vantage-web/core-data';

/**
 * Unified entry-point routing. One app, three role areas (staff `/oip`, sub, client)
 * behind their per-role guards. **Corporate (NBD) is a role inside the `/oip` staff
 * app**, not a separate area — the prototype models it as capability-gated nav +
 * national data-scoping (so `authGuard` admits corporate staff and the staff
 * nav-rail surfaces the corporate-only items). The legacy `/corp` area is **not
 * mounted** in the unified app (the standalone `vantage_*` apps were removed —
 * `vantage` is the only app). The root path runs `roleLandingGuard`, which
 * redirects by role (or to login).
 */
export const appRoutes: Route[] = [
  { path: 'auth', children: appAuthRoutes },
  {
    path: 'oip',
    canActivate: [authGuard],
    loadChildren: () => import('@vantage-web/vantage-oip').then((m) => m.oipRoutes),
  },
  {
    path: 'sub',
    canActivate: [subPortalAuthGuard],
    loadChildren: () => import('@vantage-web/vantage-sub').then((m) => m.subRoutes),
  },
  {
    path: 'client',
    canActivate: [clientPortalAuthGuard],
    loadChildren: () => import('@vantage-web/vantage-client').then((m) => m.clientRoutes),
  },
  // The emailed signing-invite landing (backend #219 / Decision J6): `{app}/sign#token=…`.
  // Deliberately UNGUARDED — the recipient has no login; the invite token in the URL fragment is
  // the credential, checked server-side by the public lookup/redeem routes. Store provided here
  // (ADR-011): the flow is one ephemeral token's lifecycle and must not outlive the page.
  {
    path: 'sign',
    providers: [SigningInviteStore],
    loadComponent: () => import('@vantage-web/vantage-client').then((m) => m.PublicSign),
  },
  { path: '', pathMatch: 'full', canActivate: [roleLandingGuard], children: [] },
  { path: '**', redirectTo: '' },
];
